"use client"
import React, { useState, createContext, ReactNode, useMemo } from 'react';

import { deleteTokenCookie, getTokenCookie, setTokenCookie, tokensPortal } from '@/libs/cookie';
import { LoaderBox } from '@/components/LoaderBox';
import { useRouter } from 'next/navigation';
import { useMountEffect } from 'primereact/hooks';

import { buscaInfoContribuinteRequest, signInContribuinteRequest } from '../services/auth';
import { Contribuinte } from '@/types';

type ChildContainerProps = {
  children: ReactNode;
}

export type ContribuinteAcesso = {
  id: number,
  cpfCnpj: string;
  razaoSocial: string;
  nomeFantasia: string;
  email: string;
  isAdmin: boolean;
}

type SignInContribuinte = {
  cpfCnpj: string;
  senha: string;
}

type AuthPortalContextProps = {
  loading: boolean;
  isAuthenticated: boolean;
  contribuinte: Contribuinte | null;
  signIn: (data: SignInContribuinte) => Promise<void>;
  logout: () => Promise<void>;
  hasPermissions: (permissions: string[]) => boolean;
}

export const AuthPortalContext = createContext({} as AuthPortalContextProps);

export const AuthPortalProvider = ({ children }: ChildContainerProps) => {
  const [loading, setLoading] = useState<boolean>(true);

  const [contribuinte, setContribuinte] = useState<Contribuinte | null>(null);
  const isAuthenticated = useMemo(() => !!contribuinte, [contribuinte]);

  const Router = useRouter();

  useMountEffect(() => {
    const token = getTokenCookie(tokensPortal.access);
    if (token) {
      buscarDadosContribuinteLogado();
    } else {
      setLoading(false);
    }
  });

  async function signIn({ cpfCnpj, senha }: SignInContribuinte) {
    const { tokenRefresh, tokenAccess } = await signInContribuinteRequest({
      cpfCnpj,
      senha
    });

    setTokenCookie(tokensPortal.refresh, tokenRefresh);
    setTokenCookie(tokensPortal.access, tokenAccess);

    await buscarDadosContribuinteLogado();

    Router.push('/portal');
  }

  async function logout() {
    deleteTokenCookie(tokensPortal.refresh);
    deleteTokenCookie(tokensPortal.access);

    setContribuinte(null);
    Router.push('/portal/login');
  }

  async function buscarDadosContribuinteLogado() {
    const contribuinte = await buscaInfoContribuinteRequest();
    setContribuinte({ ...contribuinte, permissoes: [] })

    setLoading(false);
  }

  function hasPermissions(permissions: string[]) {
    if (!permissions) return true;

    return false;
  }

  return (
    <AuthPortalContext.Provider
      value={{
        contribuinte,
        isAuthenticated,
        signIn,
        logout,
        loading,
        hasPermissions
      }}>
      {loading ? <LoaderBox /> : children}
    </AuthPortalContext.Provider>
  );
};
